// ==========================================================================
// screens/MoneyCheck.tsx — Money → Settings → Check (9 Oct 2026)
//
// James: "a lot of things look a little missaligned … lets do a full audit and
// clenaup". A standing health check, so the tidy-up isn't a one-off:
//   1. Contacts whose lines went to more than one code (a stray "OK all" or a
//      learned usual code that was wrong). Open one to tick and recode.
//   2. Accounts: opening balance missing, or the bank and the app disagree.
//   3. Lines still to reconcile, per account.
//   4. Receipts still to fill in, or waiting more than 14 days for a bank line.
// Read-only: every fix happens on the screen it links to.
// ==========================================================================
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { differenceInDays, parseISO } from "date-fns";
import { AlertTriangle, CheckCircle2, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui";
import { money, useAccounts, useCategories } from "@/lib/money";
import { useContacts, type Contact } from "@/lib/contacts";
import { useContactCoding } from "@/lib/transactions";
import { useRecSummary } from "@/lib/reconcile";
import { useReceipts } from "@/lib/receipts";
import { ContactView } from "./MoneyContacts";

function Section({ title, ok, okText, children }: { title: string; ok: boolean; okText: string; children: React.ReactNode }) {
  return (
    <Card className="space-y-3">
      <h2 className="flex items-center gap-2 font-semibold">{ok ? <CheckCircle2 className="h-5 w-5 text-good" /> : <AlertTriangle className="h-5 w-5 text-warn" />}{title}</h2>
      {ok ? <p className="text-sm text-ink-muted">{okText}</p> : children}
    </Card>
  );
}

export default function MoneyCheck() {
  const coding = useContactCoding(); const contacts = useContacts(); const cats = useCategories();
  const summary = useRecSummary(); const accounts = useAccounts(); const receipts = useReceipts();
  const [view, setView] = useState<Contact | null>(null); const nav = useNavigate();
  const catName = (id: string) => { const c = cats.data?.find((x) => x.id === id); return c ? `${c.code} ${c.is_group ? `All of ${c.name}` : c.name}` : "?"; };

  // 1. Mixed coding per contact. Family and bank contacts are expected to vary.
  const mixed = useMemo(() => {
    const by = new Map<string, Map<string, number>>();
    for (const t of coding.data ?? []) for (const s of t.transaction_splits) {
      const m = by.get(t.contact_id) ?? new Map<string, number>(); m.set(s.category_id, (m.get(s.category_id) ?? 0) + 1); by.set(t.contact_id, m);
    }
    return (contacts.data ?? []).filter((c) => !c.archived_at && !["family", "bank"].includes(c.kind)).map((c) => {
      const m = by.get(c.id) ?? new Map<string, number>();
      const off = c.default_category_id ? [...m.entries()].filter(([k]) => k !== c.default_category_id).reduce((s, [, n]) => s + n, 0) : 0;
      return { c, m, off, many: m.size > 1 };
    }).filter((x) => x.many).sort((a, b) => b.off - a.off || b.m.size - a.m.size);
  }, [coding.data, contacts.data]);

  // 2 + 3. Accounts.
  const acctRows = (summary.data ?? []).filter((s) => s.txn_count > 0).map((s) => {
    const a = accounts.data?.find((x) => x.id === s.account_id);
    const noOpening = a?.opening_balance_cents === null || a?.opening_balance_cents === undefined;
    const diff = s.statement_balance_cents !== null && !noOpening ? s.statement_balance_cents - ((a?.opening_balance_cents ?? 0) + s.app_sum_to_statement_cents) : null;
    return { s, a, noOpening, diff, noStatement: s.statement_balance_cents === null };
  });
  const acctIssues = acctRows.filter((r) => r.noOpening || r.noStatement || (r.diff !== null && r.diff !== 0));
  const toRec = acctRows.filter((r) => r.s.to_reconcile > 0);

  // 4. Receipts.
  const live = (receipts.data ?? []).filter((r) => !r.archived_at);
  const toFill = live.filter((r) => r.paid_by === "us" && !r.transaction_id && !(r.total_cents && r.receipt_date));
  const stale = live.filter((r) => r.paid_by === "us" && !r.transaction_id && r.total_cents && r.receipt_date && differenceInDays(new Date(), parseISO(r.receipt_date)) > 14);

  return (
    <div className="space-y-5">
      <p className="max-w-3xl text-sm text-ink-muted">A standing check of the money data. Anything amber is worth a look; each item links to where it's fixed. It doesn't change anything by itself.</p>

      <Section title="Contacts coded more than one way" ok={mixed.length === 0} okText="Every contact's lines are on one code.">
        <p className="text-sm text-ink-muted">Often fine (Woolworths can be groceries or household), but it's also how a wrong "OK all" or a wrong usual code shows up. Open one to see its lines, tick and recode.</p>
        <div className="divide-y divide-line/70">
          {mixed.map(({ c, m, off }) => (
            <button key={c.id} type="button" onClick={() => setView(c)} className="flex w-full items-center gap-3 py-2.5 text-left hover:bg-primary/[0.03]">
              <div className="min-w-0 flex-1">
                <div className="font-medium">{c.name}{off > 0 && <span className="ml-2 rounded-full bg-warn/10 px-2 py-0.5 text-xs text-warn">{off} not on the usual code</span>}</div>
                <div className="flex flex-wrap gap-1.5 pt-1">{[...m.entries()].sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                  <span key={k} className={`rounded-full px-2 py-0.5 text-xs ${k === c.default_category_id ? "bg-primary/10 text-primary" : "bg-surface-raised"}`}>{catName(k)} · {n}</span>
                ))}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-ink-muted" />
            </button>
          ))}
        </div>
      </Section>

      <Section title="Accounts in step with the bank" ok={acctIssues.length === 0} okText="Every account has an opening balance and agrees with its latest statement.">
        <div className="divide-y divide-line/70">
          {acctIssues.map((r) => (
            <div key={r.s.account_id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
              <span className="font-medium">{r.a?.name}</span>
              <span className="text-ink-muted">{r.noOpening ? "No opening balance yet: Reconcile → Set opening balance." : r.noStatement ? "No statement balance: import a CSV with balances or enter one." : `Out by ${money(Math.abs(r.diff!), { cents: true })}: a missing or doubled import.`}</span>
              <Link to="/money/reconcile" className="ml-auto font-medium text-primary">Reconcile</Link>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Lines to reconcile" ok={toRec.length === 0} okText="Nothing waiting.">
        <div className="flex flex-wrap gap-2">{toRec.map((r) => <span key={r.s.account_id} className="rounded-full bg-surface-raised px-3 py-1 text-sm">{r.a?.name} · <span className="num font-semibold">{r.s.to_reconcile}</span></span>)}</div>
        <Link to="/money/reconcile" className="inline-block text-sm font-medium text-primary">Go to Reconcile</Link>
      </Section>

      <Section title="Receipts" ok={toFill.length === 0 && stale.length === 0} okText="Nothing to fill in and nothing waiting long for its bank line.">
        <ul className="space-y-1 text-sm">
          {toFill.length > 0 && <li>{toFill.length} to fill in (date and total).</li>}
          {stale.length > 0 && <li>{stale.length} filled in but no matching bank line after 14 days: check the amount and date, or the account's import is behind.</li>}
        </ul>
        <Link to="/money/receipts" className="inline-block text-sm font-medium text-primary">Go to Receipts</Link>
      </Section>

      {view && <ContactView key={view.id} c={(contacts.data ?? []).find((x) => x.id === view.id) ?? view} onEdit={() => { setView(null); nav("/money/settings/contacts"); }} onClose={() => setView(null)} />}
    </div>
  );
}
